/**
 * CRM Runtime commerce surface for Mini App (flag-gated).
 * Ukrainian UI · mobile-first · presentation state only.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { AppButton } from "./ui/AppButton.js";
import {
  clearLocalCartCache,
  crmCreateOrderDraft,
  crmGetOrder,
  crmHealth,
  crmListOrders,
  crmProductDetail,
  crmProductList,
  crmSession,
  crmSubmitOrder,
  crmTrackEvent,
  crmValidateCart,
  loadLocalCartCache,
  saveLocalCartCache,
  type LocalCartLine,
} from "../api/crm-runtime-client.js";
import { getCrmFeatureFlagSnapshot, isCrmLiveCheckoutEnabled, isCrmOrderHistoryEnabled, isCrmRecommendationsEnabled } from "../lib/crm-feature-flags.js";

type Tab = "catalog" | "cart" | "checkout" | "orders" | "profile";

export function CrmCommerceApp() {
  const [tab, setTab] = useState<Tab>("catalog");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionName, setSessionName] = useState("Клієнт");
  const [customerId, setCustomerId] = useState<string | null>(null);
  const [products, setProducts] = useState<Array<Record<string, unknown>>>([]);
  const [categories, setCategories] = useState<Array<{ id: string; titleUk: string }>>([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState<string>("");
  const [detail, setDetail] = useState<Record<string, unknown> | null>(null);
  const [cart, setCart] = useState<LocalCartLine[]>(() => loadLocalCartCache());
  const [validated, setValidated] = useState<{
    lines: Array<{ productId: string; titleUk: string; qty: number; unitPriceUah: number; lineTotalUah: number }>;
    unavailable: unknown[];
    subtotalUah: number;
  } | null>(null);
  const [contactName, setContactName] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [deliveryMethod, setDeliveryMethod] = useState("nova_poshta_warehouse");
  const [city, setCity] = useState("");
  const [warehouse, setWarehouse] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("manager");
  const [submitting, setSubmitting] = useState(false);
  const [lastOrderId, setLastOrderId] = useState<string | null>(null);
  const [orders, setOrders] = useState<Array<Record<string, unknown>>>([]);
  const [orderDetail, setOrderDetail] = useState<Record<string, unknown> | null>(null);
  const [adapterNotes, setAdapterNotes] = useState<string[]>([]);
  const flags = useMemo(() => getCrmFeatureFlagSnapshot(), []);

  const refreshCatalog = useCallback(async () => {
    setError(null);
    const listParams: { q?: string; category?: string } = {};
    if (q) listParams.q = q;
    if (category) listParams.category = category;
    const list = await crmProductList(listParams);
    setProducts(list.products as Array<Record<string, unknown>>);
    setCategories(list.categories);
  }, [q, category]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const health = await crmHealth();
        if (!cancelled) {
          const notes = Object.values(health.adapters || {}).map((a) => a.labelUk);
          setAdapterNotes(notes);
        }
        const session = await crmSession();
        if (cancelled) return;
        setSessionName(session.telegram_user.first_name || session.customer.nameUk);
        setCustomerId(session.customer.id);
        await crmTrackEvent("miniapp.opened", { customerRef: session.customer.id });
        await refreshCatalog();
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? (err as { errorUk?: string }).errorUk || err.message : "Помилка");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshCatalog]);

  useEffect(() => {
    saveLocalCartCache(cart);
  }, [cart]);

  function addToCart(productId: string, titleUk: string, priceUah: number) {
    setCart((prev) => {
      const existing = prev.find((l) => l.productId === productId);
      if (existing) {
        return prev.map((l) =>
          l.productId === productId ? { ...l, qty: Math.min(20, l.qty + 1) } : l,
        );
      }
      return [...prev, { productId, qty: 1, titleUk, clientPriceUah: priceUah }];
    });
    void crmTrackEvent("cart.item_added", { productId });
  }

  function changeQty(productId: string, qty: number) {
    if (qty <= 0) {
      setCart((prev) => prev.filter((l) => l.productId !== productId));
      void crmTrackEvent("cart.item_removed", { productId });
      return;
    }
    setCart((prev) =>
      prev.map((l) => (l.productId === productId ? { ...l, qty: Math.min(20, qty) } : l)),
    );
  }

  async function validateCartNow() {
    setError(null);
    try {
      const result = await crmValidateCart(
        cart.map((l) => {
          const line: { productId: string; qty: number; clientPriceUah?: number } = {
            productId: l.productId,
            qty: l.qty,
          };
          if (l.clientPriceUah != null) line.clientPriceUah = l.clientPriceUah;
          return line;
        }),
      );
      setValidated(result);
      return result;
    } catch (err) {
      setError(err instanceof Error ? (err as { errorUk?: string }).errorUk || err.message : "Помилка кошика");
      return null;
    }
  }

  async function submitOrder() {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await crmTrackEvent("checkout.started", { customerRef: customerId });
      const v = await validateCartNow();
      if (!v || !v.lines.length) {
        setError("Кошик порожній або товари недоступні");
        return;
      }
      const draft = await crmCreateOrderDraft({
        lines: v.lines.map((l) => ({ productId: l.productId, qty: l.qty })),
        contact: { fullName: contactName, phone: contactPhone },
        delivery: { method: deliveryMethod, city, warehouse },
        payment: { method: paymentMethod },
      });
      const idempotencyKey =
        sessionStorage.getItem("crm.order.idem") || `idem-${crypto.randomUUID()}`;
      sessionStorage.setItem("crm.order.idem", idempotencyKey);
      const placed = await crmSubmitOrder({
        draftId: draft.draft.id,
        idempotencyKey,
      });
      setLastOrderId(placed.order.id);
      clearLocalCartCache();
      setCart([]);
      setValidated(null);
      sessionStorage.removeItem("crm.order.idem");
      setTab("orders");
      if (isCrmOrderHistoryEnabled()) {
        const list = await crmListOrders();
        setOrders(list.orders as Array<Record<string, unknown>>);
      }
    } catch (err) {
      setError(err instanceof Error ? (err as { errorUk?: string }).errorUk || err.message : "Помилка замовлення");
      await crmTrackEvent("checkout.validation_failed", {});
    } finally {
      setSubmitting(false);
    }
  }

  async function openOrders() {
    setTab("orders");
    if (!isCrmOrderHistoryEnabled()) return;
    try {
      const list = await crmListOrders();
      setOrders(list.orders as Array<Record<string, unknown>>);
    } catch (err) {
      setError(err instanceof Error ? (err as { errorUk?: string }).errorUk || err.message : "Помилка");
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <p className="text-sm opacity-70">Завантаження CRM каталогу…</p>
      </div>
    );
  }

  return (
    <div className="app-shell flex min-h-screen max-w-full flex-col overflow-x-hidden bg-[var(--tg-theme-bg-color,#fff)] text-[var(--tg-theme-text-color,#111)]">
      <header className="sticky top-0 z-20 border-b border-black/10 bg-[var(--tg-theme-bg-color,#fff)]/95 px-4 py-3 backdrop-blur">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-orange-700">AsiaStyle</p>
        <h1 className="text-lg font-semibold">Вітаємо, {sessionName}</h1>
        <p className="text-xs opacity-60">Канал: Telegram Mini App · CRM Runtime</p>
      </header>

      {error ? (
        <div className="mx-4 mt-3 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
          <button type="button" className="ml-2 underline" onClick={() => setError(null)}>
            Закрити
          </button>
        </div>
      ) : null}

      {adapterNotes.length > 0 ? (
        <div className="mx-4 mt-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {adapterNotes.map((n) => (
            <div key={n}>• {n}</div>
          ))}
        </div>
      ) : null}

      <main className="flex-1 overflow-x-hidden px-4 py-3 pb-24">
        {tab === "catalog" && !detail ? (
          <section className="space-y-3">
            <div className="flex gap-2">
              <input
                className="min-h-11 flex-1 rounded-lg border border-black/15 px-3"
                placeholder="Пошук товарів"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
              <AppButton type="button" onClick={() => void refreshCatalog()}>
                Шукати
              </AppButton>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className={`min-h-10 rounded-full px-3 text-sm ${!category ? "bg-orange-600 text-white" : "bg-black/5"}`}
                onClick={() => setCategory("")}
              >
                Усі
              </button>
              {categories.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={`min-h-10 rounded-full px-3 text-sm ${category === c.id ? "bg-orange-600 text-white" : "bg-black/5"}`}
                  onClick={() => {
                    setCategory(c.id);
                    void refreshCatalog();
                  }}
                >
                  {c.titleUk}
                </button>
              ))}
            </div>
            {products.length === 0 ? (
              <p className="py-8 text-center text-sm opacity-60">Товарів не знайдено</p>
            ) : (
              <ul className="space-y-3">
                {products.map((p) => (
                  <li key={String(p.id)} className="rounded-xl border border-black/10 p-3">
                    <button
                      type="button"
                      className="w-full text-left"
                      onClick={() => {
                        void (async () => {
                          const d = await crmProductDetail(String(p.id));
                          setDetail(d.product as Record<string, unknown>);
                          void crmTrackEvent("product.viewed", { productId: String(p.id) });
                        })();
                      }}
                    >
                      <p className="font-medium">{String(p.titleUk)}</p>
                      <p className="text-sm opacity-70">
                        {String(p.priceUah)} грн · {p.available ? "В наявності" : "Немає"}
                      </p>
                    </button>
                    {p.available ? (
                      <AppButton
                        type="button"
                        className="mt-2"
                        onClick={() => addToCart(String(p.id), String(p.titleUk), Number(p.priceUah))}
                      >
                        У кошик
                      </AppButton>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </section>
        ) : null}

        {tab === "catalog" && detail ? (
          <section className="space-y-3">
            <button type="button" className="min-h-11 text-sm underline" onClick={() => setDetail(null)}>
              ← Назад до каталогу
            </button>
            <h2 className="text-xl font-semibold">{String(detail.titleUk)}</h2>
            <p>{String(detail.priceUah)} грн</p>
            <p className="text-sm">{detail.available ? "В наявності" : "Немає в наявності"}</p>
            {detail.available ? (
              <AppButton
                type="button"
                onClick={() => addToCart(String(detail.id), String(detail.titleUk), Number(detail.priceUah))}
              >
                Додати в кошик
              </AppButton>
            ) : null}
            {isCrmRecommendationsEnabled() && detail.recommendationLabelsUk ? (
              <div className="space-y-2 pt-2">
                <p className="text-sm font-medium">{(detail.recommendationLabelsUk as Record<string, string>).similar}</p>
                <p className="text-sm font-medium">{(detail.recommendationLabelsUk as Record<string, string>).boughtTogether}</p>
                <p className="text-sm font-medium">{(detail.recommendationLabelsUk as Record<string, string>).completeOrder}</p>
              </div>
            ) : null}
          </section>
        ) : null}

        {tab === "cart" ? (
          <section className="space-y-3">
            <h2 className="text-lg font-semibold">Кошик</h2>
            {cart.length === 0 ? (
              <p className="py-8 text-center text-sm opacity-60">Кошик порожній</p>
            ) : (
              <ul className="space-y-2">
                {cart.map((l) => (
                  <li key={l.productId} className="flex items-center justify-between gap-2 rounded-lg border border-black/10 p-3">
                    <div className="min-w-0">
                      <p className="truncate font-medium">{l.titleUk || l.productId}</p>
                      <div className="mt-1 flex items-center gap-2">
                        <button type="button" className="min-h-11 min-w-11 rounded bg-black/5" onClick={() => changeQty(l.productId, l.qty - 1)}>−</button>
                        <span>{l.qty}</span>
                        <button type="button" className="min-h-11 min-w-11 rounded bg-black/5" onClick={() => changeQty(l.productId, l.qty + 1)}>+</button>
                      </div>
                    </div>
                    <button type="button" className="text-sm text-red-700 underline" onClick={() => changeQty(l.productId, 0)}>
                      Видалити
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <AppButton type="button" onClick={() => void validateCartNow()}>
              Перевірити ціни
            </AppButton>
            {validated ? (
              <p className="text-sm">
                Підсумок (сервер): {validated.subtotalUah} грн
                {validated.unavailable.length ? ` · недоступно: ${validated.unavailable.length}` : ""}
              </p>
            ) : null}
            {isCrmLiveCheckoutEnabled() ? (
              <AppButton type="button" onClick={() => setTab("checkout")}>
                Оформити
              </AppButton>
            ) : (
              <p className="text-sm text-amber-800">
                Оформлення замовлення тимчасово недоступне. Спробуйте пізніше.
              </p>
            )}
          </section>
        ) : null}

        {tab === "checkout" ? (
          <section className="space-y-3">
            <button type="button" className="text-sm underline" onClick={() => setTab("cart")}>← Кошик</button>
            <h2 className="text-lg font-semibold">Оформлення</h2>
            <ol className="list-decimal space-y-1 pl-5 text-sm opacity-80">
              <li>Контактні дані</li>
              <li>Доставка</li>
              <li>Відділення / адреса</li>
              <li>Оплата</li>
              <li>Перевірка</li>
              <li>Створення замовлення</li>
            </ol>
            <label className="block text-sm">
              Імʼя
              <input className="mt-1 min-h-11 w-full rounded-lg border px-3" value={contactName} onChange={(e) => setContactName(e.target.value)} />
            </label>
            <label className="block text-sm">
              Телефон
              <input className="mt-1 min-h-11 w-full rounded-lg border px-3" value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} />
            </label>
            <label className="block text-sm">
              Доставка
              <select className="mt-1 min-h-11 w-full rounded-lg border px-3" value={deliveryMethod} onChange={(e) => setDeliveryMethod(e.target.value)}>
                <option value="nova_poshta_warehouse">Нова Пошта (відділення) — ще не підключено</option>
                <option value="nova_poshta_courier">Нова Пошта (курʼєр) — ще не підключено</option>
                <option value="pickup">Самовивіз</option>
                <option value="other">Інше / менеджер</option>
              </select>
            </label>
            <label className="block text-sm">
              Місто
              <input className="mt-1 min-h-11 w-full rounded-lg border px-3" value={city} onChange={(e) => setCity(e.target.value)} />
            </label>
            <label className="block text-sm">
              Відділення
              <input className="mt-1 min-h-11 w-full rounded-lg border px-3" value={warehouse} onChange={(e) => setWarehouse(e.target.value)} />
            </label>
            <label className="block text-sm">
              Оплата
              <select className="mt-1 min-h-11 w-full rounded-lg border px-3" value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)}>
                <option value="manager">Через менеджера</option>
                <option value="cod">Накладений платіж</option>
                <option value="novapay">NovaPay — ще не підключено</option>
              </select>
            </label>
            <AppButton type="button" disabled={submitting} onClick={() => void submitOrder()}>
              {submitting ? "Створення…" : "Підтвердити замовлення"}
            </AppButton>
            {lastOrderId ? <p className="text-sm text-green-800">Замовлення: {lastOrderId}</p> : null}
          </section>
        ) : null}

        {tab === "orders" ? (
          <section className="space-y-3">
            <h2 className="text-lg font-semibold">Мої замовлення</h2>
            {!isCrmOrderHistoryEnabled() ? (
              <p className="text-sm text-amber-800">
                Історія замовлень тимчасово недоступна. Спробуйте пізніше.
              </p>
            ) : orders.length === 0 ? (
              <p className="py-8 text-center text-sm opacity-60">Замовлень поки немає</p>
            ) : (
              <ul className="space-y-2">
                {orders.map((o) => (
                  <li key={String(o.id)}>
                    <button
                      type="button"
                      className="min-h-11 w-full rounded-lg border border-black/10 p-3 text-left"
                      onClick={() => {
                        void (async () => {
                          const d = await crmGetOrder(String(o.id));
                          setOrderDetail(d.order as Record<string, unknown>);
                        })();
                      }}
                    >
                      <p className="font-medium">{String(o.id)}</p>
                      <p className="text-sm opacity-70">{String(o.status)} · {String(o.totalUah)} грн</p>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {orderDetail ? (
              <div className="rounded-lg border border-black/10 p-3 text-sm">
                <p className="font-medium">Деталі {String(orderDetail.id)}</p>
                <p>Статус: {String(orderDetail.status)}</p>
                <p className="mt-2 font-medium">Timeline</p>
                <ul className="mt-1 space-y-1">
                  {((orderDetail.timeline || orderDetail.history || []) as Array<{ titleUk: string; at: string }>).map((h, i) => (
                    <li key={`${h.at}-${i}`}>• {h.titleUk}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </section>
        ) : null}

        {tab === "profile" ? (
          <section className="space-y-2 text-sm">
            <h2 className="text-lg font-semibold">Профіль</h2>
            <p>Клієнт CRM: {customerId || "—"}</p>
            <p>Прапорці:</p>
            <ul>
              {Object.entries(flags).map(([k, v]) => (
                <li key={k}>{k}: {v ? "увімкнено" : "вимкнено"}</li>
              ))}
            </ul>
          </section>
        ) : null}
      </main>

      <nav className="fixed bottom-0 left-0 right-0 z-30 flex border-t border-black/10 bg-[var(--tg-theme-bg-color,#fff)] px-2 py-2">
        {(
          [
            ["catalog", "Каталог"],
            ["cart", `Кошик (${cart.reduce((a, l) => a + l.qty, 0)})`],
            ["orders", "Замовлення"],
            ["profile", "Профіль"],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={`min-h-12 flex-1 rounded-lg text-xs font-medium ${tab === id || (id === "catalog" && tab === "checkout") ? "bg-orange-600 text-white" : ""}`}
            onClick={() => {
              if (id === "orders") void openOrders();
              else setTab(id);
              setDetail(null);
            }}
          >
            {label}
          </button>
        ))}
      </nav>
    </div>
  );
}
